const SCHEMA_TYPE_PATTERN = /^[a-z_][a-z0-9_]*$/i;

const assertSafeSchemaType = (schemaType) => {
  if (!SCHEMA_TYPE_PATTERN.test(schemaType)) {
    throw new Error(`Invalid schemaType: ${schemaType}`);
  }
};

// Every nesting level selects the same type metadata so enums surface wherever they sit
// (a nullable `[Enum!]` field puts the ENUM at the third level: LIST -> NON_NULL -> ENUM).
const TYPE_META = "name kind description enumValues { name description }";
const SUB_FIELDS = `fields { name description type { ${TYPE_META} ofType { ${TYPE_META} ofType { ${TYPE_META} } } } }`;

// The `update_input` selection fetches the generated Update<Type>Input, which omits
// the row's pk column (it travels as the separate `id` mutation argument) - so its
// inputFields are the ground truth for telling the pk apart from ordinary *_id data
// columns. Null for schemas without an update mutation (mapping tables).
export const INTROSPECT_DOC = `
  query ExploreIntrospect($name: String!, $updateInputName: String!, $createInputName: String!) {
    update_input: __type(name: $updateInputName) {
      inputFields { name }
    }
    create_input: __type(name: $createInputName) {
      inputFields { name type { kind } }
    }
    __type(name: $name) {
      fields {
        name
        description
        type {
          ${TYPE_META}
          ${SUB_FIELDS}
          ofType {
            ${TYPE_META}
            ${SUB_FIELDS}
            ofType {
              ${TYPE_META}
              ${SUB_FIELDS}
              ofType { ${TYPE_META} fields { name description type { ${TYPE_META} ofType { ${TYPE_META} } } } }
            }
          }
        }
      }
    }
  }
`;

const GQL_OBJECT_KIND = "OBJECT";

const getObjectSubFieldNames = (type) => {
  let current = type;
  while (current) {
    if (current.kind === GQL_OBJECT_KIND && Array.isArray(current.fields) && current.fields.length) {
      return current.fields.map((f) => f.name);
    }
    current = current.ofType;
  }
  return null;
};

const buildFieldSelection = (field) => {
  assertSafeSchemaType(field.name);
  const subFields = getObjectSubFieldNames(field.type);
  if (!subFields) return field.name;
  subFields.forEach(assertSafeSchemaType);
  return `${field.name} { ${subFields.join(" ")} }`;
};

export const buildVersionsQuery = (schemaType) => {
  assertSafeSchemaType(schemaType);
  return `query ExploreVersions { ${schemaType}_versions }`;
};

export const buildExploreQuery = ({ schemaType, fields, withSort = false, withPriority = false }) => {
  assertSafeSchemaType(schemaType);
  const selection = fields.map(buildFieldSelection).join(" ");
  const sortVariable = withSort ? ", $sort: SortInput" : "";
  const sortArgument = withSort ? ", sort: $sort" : "";
  const priorityVariable = withPriority ? ", $priority_pks: [String!]" : "";
  const priorityArgument = withPriority ? ", priority_pks: $priority_pks" : "";
  return `
    query Explore($version: String!, $filters: JSON, $pagination: PaginationInput${sortVariable}${priorityVariable}) {
      ${schemaType}(version: $version, filters: $filters, pagination: $pagination${sortArgument}${priorityArgument}) {
        items { ${selection} }
        total_count
      }
    }
  `;
};

export const buildFieldValuesQuery = (schemaType) => {
  assertSafeSchemaType(schemaType);
  return `
    query FieldValues($field: String!, $version: String!) {
      ${schemaType}_field_values(field: $field, version: $version)
    }
  `;
};

const toPascalCase = (schemaType) =>
  schemaType.split("_").map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join("");

// Strawberry camelizes GraphQL *type* names while query/mutation fields stay
// snake_case, so introspecting `provider_location_association` needs the type
// name `providerLocationAssociation`.
export const toGqlTypeName = (schemaType) => {
  const pascal = toPascalCase(schemaType);
  return pascal.charAt(0).toLowerCase() + pascal.slice(1);
};

const toInputTypeName = (schemaType, mode) => `${mode}${toPascalCase(schemaType)}Input`;

export const toUpdateInputTypeName = (schemaType) => {
  assertSafeSchemaType(schemaType);
  return toInputTypeName(schemaType, "Update");
};

export const toCreateInputTypeName = (schemaType) => {
  assertSafeSchemaType(schemaType);
  return toInputTypeName(schemaType, "Create");
};

const VALIDATION_SCOPE_SELECTION = "schema_type pk errors { path type details error_severity_level }";

export const MUTATION_VALIDATION_SELECTION = `validation_results { ${VALIDATION_SCOPE_SELECTION} }`;

export const extractMutationPayload = (data) => Object.values(data ?? {})[0];

export const extractMutationValidationScopes = (data) => {
  const payload = extractMutationPayload(data);
  return Array.isArray(payload?.validation_results) ? payload.validation_results : [];
};

export const buildCreateMutation = (schemaType) => {
  assertSafeSchemaType(schemaType);
  const inputType = toInputTypeName(schemaType, "Create");
  return `
    mutation CreateRow($version: String!, $input: ${inputType}!) {
      create_${schemaType}(version: $version, input: $input) { id ${MUTATION_VALIDATION_SELECTION} }
    }
  `;
};

export const buildUpdateMutation = (schemaType) => {
  assertSafeSchemaType(schemaType);
  const inputType = toInputTypeName(schemaType, "Update");
  return `
    mutation UpdateRow($version: String!, $id: String!, $input: ${inputType}!) {
      update_${schemaType}(version: $version, id: $id, input: $input) { id ${MUTATION_VALIDATION_SELECTION} }
    }
  `;
};

export const buildDeleteMutation = (schemaType) => {
  assertSafeSchemaType(schemaType);
  return `
    mutation DeleteRow($version: String!, $id: String!) {
      delete_${schemaType}(version: $version, id: $id) { deleted ${MUTATION_VALIDATION_SELECTION} }
    }
  `;
};

export const buildBulkDeleteMutation = (schemaType) => {
  assertSafeSchemaType(schemaType);
  return `
    mutation DeleteManyRows($version: String!, $ids: [String!]!) {
      delete_many_${schemaType}(version: $version, ids: $ids) { count ${MUTATION_VALIDATION_SELECTION} }
    }
  `;
};

export const buildBulkUpsertMutation = (schemaType) => {
  assertSafeSchemaType(schemaType);
  const inputType = toInputTypeName(schemaType, "Create");
  return `
    mutation BulkUpsertRows($version: String!, $inputs: [${inputType}!]!) {
      bulk_upsert_${schemaType}(version: $version, inputs: $inputs) { count ${MUTATION_VALIDATION_SELECTION} }
    }
  `;
};

export const buildBulkReplaceMutation = (schemaType) => {
  assertSafeSchemaType(schemaType);
  const inputType = toInputTypeName(schemaType, "Create");
  return `
    mutation BulkReplaceRows($version: String!, $inputs: [${inputType}!]!) {
      bulk_replace_${schemaType}(version: $version, inputs: $inputs) { count ${MUTATION_VALIDATION_SELECTION} }
    }
  `;
};
